/**
 * Bot / spam heuristics for the public contact + careers forms.
 *
 * The same ruleset runs server-side in figazuresql
 * (middleware/contactSpamGuard.js) because the CRM autoscaffold endpoint is
 * publicly writable — bots POST to it directly and never load this bundle.
 * Keep the two files in sync when you change a rule.
 *
 * Every check returns a short reason string on rejection, or null when clean.
 */

// Minimum seconds a real person needs to fill the form out. The bots submit
// instantly; observed spam arrived in sub-second bursts.
export const MIN_FILL_SECONDS = 5;

// Per-browser throttle.
const THROTTLE_KEY = "sk12_last_submit";
const THROTTLE_SECONDS = 60;

const VOWELS = "aeiouy";

/** Strip Gmail's ignored dots and +tags so g.i.we.l.u@gmail.com === giwelu@gmail.com. */
export function normalizeEmail(email) {
    const raw = String(email || "").trim().toLowerCase();
    const at = raw.lastIndexOf("@");
    if (at < 1) return raw;
    let local = raw.slice(0, at);
    const domain = raw.slice(at + 1);
    const plus = local.indexOf("+");
    if (plus > -1) local = local.slice(0, plus);
    if (domain === "gmail.com" || domain === "googlemail.com") {
        local = local.replace(/\./g, "");
        return `${local}@gmail.com`;
    }
    return `${local}@${domain}`;
}

/**
 * Known-abuser list, stored normalized. The observed campaign rotated the dot
 * placement on four Gmail accounts to look like distinct senders.
 */
const BLOCKED_EMAILS = new Set([
    "giwelupovog969@gmail.com",
    "ihamizap827@gmail.com",
    "uwutovace21@gmail.com",
    "uporepu599@gmail.com",
]);

/**
 * Looks like a random token: one long run of letters, no spaces, mixed case,
 * and no plausible word structure. Matches "sXvCfRRMzYMdNGTTjmomE".
 */
function isRandomToken(value) {
    const s = String(value || "").trim();
    if (s.length < 12 || /\s/.test(s)) return false;
    if (!/^[A-Za-z]+$/.test(s)) return false;
    const hasUpper = /[A-Z]/.test(s);
    const hasLower = /[a-z]/.test(s);
    const caseFlips = s.split("").filter((c, i) => i > 0 && /[A-Z]/.test(c) !== /[A-Z]/.test(s[i - 1])).length;
    return hasUpper && hasLower && caseFlips >= 4;
}

/**
 * Unpronounceable word: too few vowels, or a long consonant run.
 * Matches "Tdiyqfzb", "Xhapyu", "Frnjjmce" without tripping on real names.
 */
function isGibberishWord(word) {
    const w = String(word || "").toLowerCase().replace(/[^a-z]/g, "");
    if (w.length < 5) return false;
    const vowels = w.split("").filter((c) => VOWELS.includes(c)).length;
    if (vowels / w.length < 0.22) return true;
    return /[bcdfghjklmnpqrstvwxz]{5,}/.test(w);
}

/** Every word in the name is unpronounceable → generated name. */
export function checkName(name) {
    const s = String(name || "").trim();
    if (s.length < 2) return "name too short";
    if (/https?:|www\.|<[a-z]/i.test(s)) return "name contains markup or a link";
    if (isRandomToken(s.replace(/\s+/g, ""))) return "name looks machine-generated";
    const words = s.split(/\s+/).filter((w) => w.length >= 5);
    if (words.length >= 2 && words.every(isGibberishWord)) return "name looks machine-generated";
    return null;
}

export function checkEmail(email) {
    const raw = String(email || "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(raw)) return "invalid email address";
    const normalized = normalizeEmail(raw);
    if (BLOCKED_EMAILS.has(normalized)) return "blocked sender";
    // Dot-obfuscation: real people do not write g.i.w.e.l@gmail.com.
    const local = raw.slice(0, raw.lastIndexOf("@"));
    const dots = (local.match(/\./g) || []).length;
    if (dots >= 3) return "obfuscated email address";
    if (/(^|\.)[a-z]\.[a-z](\.|$)/i.test(local) && dots >= 2) return "obfuscated email address";
    return null;
}

/** North American numbering plan: 10 digits, area + exchange start 2-9. */
export function checkPhone(phone) {
    const digits = String(phone || "").replace(/\D/g, "");
    const nanp = digits.length === 11 && digits[0] === "1" ? digits.slice(1) : digits;
    if (nanp.length !== 10) return "phone number must be 10 digits";
    if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(nanp)) return "not a valid US phone number";
    if (/^(\d)\1{9}$/.test(nanp)) return "not a valid US phone number";
    return null;
}

const SOLICITATION_PHRASES = [
    "respond with stop",
    "to optout",
    "to opt out",
    "no-obligation quote",
    "complimentary bid",
    "seo services",
    "guest post",
    "backlink",
    "crypto",
];

export function checkMessage(message) {
    const s = String(message || "").trim();
    if (isRandomToken(s)) return "message looks machine-generated";
    // A real message has sentence structure; a token dump has one long "word".
    const words = s.split(/\s+/);
    if (words.length < 3 && s.length > 15) return "message looks machine-generated";
    const links = (s.match(/https?:\/\/|www\./gi) || []).length;
    if (links > 2) return "too many links";
    if (/<\s*(a|script|iframe|img)\b/i.test(s)) return "message contains markup";
    const lower = s.toLowerCase();
    if (SOLICITATION_PHRASES.some((p) => lower.includes(p))) return "unsolicited sales message";
    return null;
}

/**
 * High-confidence bot signals only. These are the rules that are safe to
 * enforce server-side, where a false positive silently loses a real lead.
 * Deliberately does NOT include "message too short" — real prospects do submit
 * two-word messages ("Jay", "civil rights") and we would rather keep those.
 */
export function botSignals({ name, email, phone, message, honeypot, elapsedMs }) {
    if (honeypot) return "honeypot";
    if (typeof elapsedMs === "number" && elapsedMs < MIN_FILL_SECONDS * 1000) return "submitted too quickly";
    return checkName(name) || checkEmail(email) || checkMessage(message) || null;
}

/**
 * Everything in botSignals plus the stricter formatting rules we can afford in
 * the browser, where a rejection just shows the visitor a fixable message.
 */
export function checkSubmission(input) {
    const bot = botSignals(input);
    if (bot) return bot;
    if (String(input.message || "").trim().length < 10) return "please add a little more detail";
    return checkPhone(input.phone);
}

/** Browser-local throttle so one visitor cannot hammer the form. */
export function throttled() {
    try {
        const last = Number(window.localStorage.getItem(THROTTLE_KEY) || 0);
        return last > 0 && Date.now() - last < THROTTLE_SECONDS * 1000;
    } catch {
        return false;
    }
}

export function markSubmitted() {
    try {
        window.localStorage.setItem(THROTTLE_KEY, String(Date.now()));
    } catch {
        /* private browsing — throttle is best-effort */
    }
}
